// Feature levels, as the kit's UpdateCheck.swift has them (docs/22-architecture-for-later.md; AGENTS.md, boundary 7).
//
// One number for all devices goes up by one whenever the account can hold something older versions can't show. Each
// app says which level it shows; the web shows level 2 (templates, shares, repeats that end, repeating projects), so it
// never has to say "your account holds things this version can't show" (docs/47). Two marks on the account's document,
// set only when needed and only ever raised:
// - suggested (`suggestedFeatureLevel`): something in the account is shown in full only from that level on. A version
//   below it keeps working and suggests the update.
// - minimum (`minimumFeatureLevel`): a version below it would damage the account by taking part. It writes nothing
//   and asks for the update (for the web: a reload).
// Versions before levels (iPhone 1.0) read only `suggestedVersion` / `minimumVersion`, so a mark above level 1 also
// writes "1.1.0" there.

import type { Favorite, Project, Situation, Todo } from "./records";
import type { RepeatRule } from "./types";

export const FeatureLevel = {
  /** What the first versions show (iPhone 1.0). */
  first: 1,
  /** Templates, sharing, repeats that end on a day, repeating projects (iPhone 1.1, Mac 1.0, the web). */
  templatesAndSharing: 2,
  /**
   * Habits (docs/63): older versions see their days as plain to-dos in Today and can tick them. And repeats on days of
   * the month (`monthDays`, docs/67): older versions say "Every month" and repeat from the start, which is on such a
   * day; changing the repeat or making the next copy there drops the days, and the series repeats from its start.
   */
  habits: 3,
  /** The same level, by the name of its second feature. */
  repeatDays: 3,
} as const;

/** The level the web shows. Raised only when the web shows everything up to the new level (AGENTS.md, boundary 7). */
export const WEB_FEATURE_LEVEL = FeatureLevel.habits;

/** The key of the web in Remote Config's `feature_versions` (beside "ios", "mac" and "watch"). */
export const WEB_PLATFORM = "web";

/**
 * Which version of each app first shows a level, as far as this build knows (FeatureLevel.builtInVersions, with the
 * web's own entry added); Remote Config's `feature_versions` adds later ones.
 */
export const BUILT_IN_VERSIONS: Readonly<Record<number, Readonly<Record<string, string>>>> = {
  [FeatureLevel.templatesAndSharing]: { ios: "1.1.0", mac: "1.0.0", watch: "1.1.0", web: "1.0.0" },
  [FeatureLevel.habits]: { ios: "1.1.0", mac: "1.1.0", web: "1.1.0" },
};

/** What versions before levels read instead: anything from 1.1.0 on is more than they show. */
export const LEGACY_VERSION = "1.1.0";

/** A mark the account needs because of something the user did. */
export interface FeatureMark {
  level: number;
  /** Versions below the level stop syncing (minimum) instead of suggesting the update (suggested). */
  strictly: boolean;
}

/** The mark every level-2 feature sets when it is first used: suggested, not strict (Store.swift). */
export const LEVEL_2_MARK: FeatureMark = { level: FeatureLevel.templatesAndSharing, strictly: false };

/** The first habit (Store.addHabit, makeHabit): suggested, not strict; older versions tick its days as to-dos. */
export const MARK_FOR_HABIT: FeatureMark = { level: FeatureLevel.habits, strictly: false };

/** "0.8.9" is older than "0.8.10" and "1.0"; anything that isn't digits and dots counts as nothing (UpdateCheck.isOlder). */
export function isOlder(version: string, than: string): boolean {
  const parts = (text: string) => text.split(".").filter((part) => part !== "").map((part) => (/^\d+$/.test(part) ? Number(part) : 0));
  const a = parts(version);
  const b = parts(than);
  for (let index = 0; index < Math.max(a.length, b.length); index++) {
    const x = a[index] ?? 0;
    const y = b[index] ?? 0;
    if (x !== y) return x < y;
  }
  return false;
}

/** The level an old version mark stands for: such marks were written only for what level 2 brings. */
export function levelFromLegacy(version: string | undefined): number | undefined {
  if (version === undefined || version === "") return undefined;
  return isOlder("1.0.99", version) ? FeatureLevel.templatesAndSharing : FeatureLevel.first;
}

/** The account's marks as levels; absent when unmarked. */
export interface AccountMarks {
  minimum?: number;
  suggested?: number;
}

/** What the user's document says (UpdateCheck.account): its levels, or, from versions before levels, its version marks. */
export function accountMarks(document: {
  minimumFeatureLevel?: number;
  suggestedFeatureLevel?: number;
  minimumVersion?: string;
  suggestedVersion?: string;
}): AccountMarks {
  const marks: AccountMarks = {};
  const minimum = document.minimumFeatureLevel ?? levelFromLegacy(document.minimumVersion);
  const suggested = document.suggestedFeatureLevel ?? levelFromLegacy(document.suggestedVersion);
  if (minimum !== undefined) marks.minimum = minimum;
  if (suggested !== undefined) marks.suggested = suggested;
  return marks;
}

/**
 * This version may not take part: it writes nothing and asks for a reload. `globalMinimum` is a minimum version for
 * everyone from Remote Config (as `minimum_version_ios`), compared with `current`, the web's own version.
 */
export function isBlocked(marks: AccountMarks, level: number = WEB_FEATURE_LEVEL, global?: { minimum?: string; current: string }): boolean {
  if (marks.minimum !== undefined && level < marks.minimum) return true;
  return global?.minimum !== undefined && global.minimum !== "" && isOlder(global.current, global.minimum);
}

/** A newer version would show more of the account. */
export function suggestsUpdate(marks: AccountMarks, containsUnknown: boolean, level: number = WEB_FEATURE_LEVEL): boolean {
  return containsUnknown || (marks.suggested !== undefined && level < marks.suggested);
}

/** What a newer version would bring (UpdateCheck.Suggestion). */
export type Suggestion =
  /** This version shows everything ("1.1"). */
  | { kind: "version"; version: string }
  /** A newer version, not known which. */
  | { kind: "newer" }
  /** No version of this app shows it yet: nothing to update to. */
  | { kind: "notYet" };

/**
 * What the note says. `remoteVersions` is Remote Config's `feature_versions` (`{"3": {"ios": "1.2.0"}}`), read over the
 * built-in table.
 */
export function suggestion(
  marks: AccountMarks, remoteVersions: Record<string, Record<string, string>> = {}, level: number = WEB_FEATURE_LEVEL, platform = WEB_PLATFORM,
): Suggestion {
  const needed = marks.suggested;
  if (needed === undefined || level >= needed) return { kind: "newer" };
  const apps = remoteVersions[String(needed)] ?? BUILT_IN_VERSIONS[needed];
  if (!apps) return { kind: "newer" };
  const version = apps[platform];
  return version === undefined ? { kind: "notYet" } : { kind: "version", version: shortened(version) };
}

/** "1.1.0" is said "1.1"; "1.0.1" stays. */
export function shortened(version: string): string {
  return version.endsWith(".0") && version.split(".").length === 3 ? version.slice(0, -2) : version;
}

/**
 * The fields that mark the account (AccountMark.fields), merged into `users/{uid}`; null when it is marked so
 * already: a mark only ever goes up. Above level 1 the version field beside it says "1.1.0" for versions before levels.
 */
export function markFields(mark: FeatureMark, marks: AccountMarks): Record<string, number | string> | null {
  const known = mark.strictly ? marks.minimum : marks.suggested;
  if (known !== undefined && known >= mark.level) return null;
  const fields: Record<string, number | string> = { [mark.strictly ? "minimumFeatureLevel" : "suggestedFeatureLevel"]: mark.level };
  if (mark.level > FeatureLevel.first) fields[mark.strictly ? "minimumVersion" : "suggestedVersion"] = LEGACY_VERSION;
  return fields;
}

/** The marks after `markFields` was written, so a second use in the same session writes nothing. */
export function marked(mark: FeatureMark, marks: AccountMarks): AccountMarks {
  const key = mark.strictly ? "minimum" : "suggested";
  const known = marks[key];
  return known !== undefined && known >= mark.level ? marks : { ...marks, [key]: mark.level };
}

// MARK: - What marks the account (Store.swift)

/** Setting an end on a repeat where there was none (`Store.update(_ todo:)`): older versions repeat past it. */
export function markForTodoChange(old: Pick<Todo, "repeats">, next: Pick<Todo, "repeats">): FeatureMark | null {
  return next.repeats?.endsOn !== undefined && old.repeats?.endsOn === undefined ? LEVEL_2_MARK : null;
}

/** The first repeat on days of the month (docs/67; `Store.markRepeatDays`): older versions can't show it, nothing is damaged. */
export const MARK_FOR_REPEAT_DAYS: FeatureMark = { level: FeatureLevel.repeatDays, strictly: false };

/** A repeat that gets days of the month where it had none (`Store.update`, to-dos and projects alike). */
export function markForRepeatDays(old: { repeats?: RepeatRule }, next: { repeats?: RepeatRule }): FeatureMark | null {
  return hasMonthDays(next.repeats) && !hasMonthDays(old.repeats) ? MARK_FOR_REPEAT_DAYS : null;
}

/** Whether a rule uses its days of the month (`RepeatRule.activeMonthDays`). */
function hasMonthDays(rule: RepeatRule | undefined): boolean {
  return rule !== undefined && rule.unit === "month" && rule.monthPosition === undefined
    && (rule.monthDays ?? []).some((day) => Number.isInteger(day) && day >= 1 && day <= 31);
}

/** Making a project repeat (`Store.update(_ project:)`): older versions complete it without its next copy. */
export function markForProjectChange(old: Pick<Project, "repeats">, next: Pick<Project, "repeats">): FeatureMark | null {
  return next.repeats !== undefined && old.repeats === undefined ? LEVEL_2_MARK : null;
}

/** A template made (added, from a project, or the examples): versions before templates don't show them. */
export const MARK_FOR_TEMPLATE: FeatureMark = LEVEL_2_MARK;

/**
 * A share this device did not know arrived (`Store.noteNewShares`): versions before sharing can't show it, the shared
 * list leaves their view without a word. `known` are the share ids seen before.
 */
export function markForShares(known: Iterable<string>, arrived: readonly { id: string }[]): FeatureMark | null {
  const seen = new Set(known);
  return arrived.some((share) => !seen.has(share.id)) ? LEVEL_2_MARK : null;
}

/**
 * Whether the account holds something only a newer version shows (Store.containsUnknownValues): a kind of favorite,
 * project or situation this version doesn't know. It is kept and works on; the web suggests the update.
 */
export function containsUnknownValues(data: {
  favorites?: readonly Favorite[];
  projects: readonly Project[];
  situations?: readonly Situation[];
}): boolean {
  if (data.favorites?.some((favorite) => favorite.kind === "unknown")) return true;
  if (data.projects.some((project) => typeof project.kind === "object")) return true;
  return (data.situations ?? []).some((situation) =>
    situation.trigger.kind === "unknown" || situation.hides.some((scope) => scope.kind === "unknown"));
}
